const ACCESS_CODE = '0109';
const ACCESS_EVENT = 'ist1947:early-access-granted';
const DEFAULT_LAUNCH_AT = '2026-09-30T00:00:00+05:30';
const STORAGE_KEY = 'ist1947:early-access';
const STORAGE_VALUE = `granted:${ACCESS_CODE}`;
const PAGE_LOCKED_ATTRIBUTE = 'data-early-access-page-locked';
const SIGNUP_RETURN_PARAM = 'early_access_signup';
const SIGNUP_TIMEOUT_MS = 8000;
// `type="email"` is lenient on its own (it accepts `a@b`), so pair it with this.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Mirrors the locked state of the page's own gate onto the document element.
 *
 * The gate only wraps the buy-buttons block, but a product page can offer a
 * second route to the cart that sits outside it — the sticky add-to-cart bar in
 * `sections/product-information.liquid`. That bar hides itself off this
 * attribute, so a locked product stays locked no matter how far the shopper has
 * scrolled. Card gates are skipped: they are grid furniture, not the page's own
 * purchase form.
 *
 * Note the name: the locked panel inside each gate already carries
 * `data-early-access-locked`, so the document-level flag is deliberately
 * `data-early-access-page-locked` to keep the two selectors apart.
 */
function syncPageLockedState() {
  const locked = Array.from(document.querySelectorAll('early-access-gate')).some(
    (gate) => !gate.classList.contains('quick-add-access-gate') && !gate.hasAttribute('data-access-granted')
  );

  document.documentElement.toggleAttribute(PAGE_LOCKED_ATTRIBUTE, locked);
}

class EarlyAccessGate extends HTMLElement {
  constructor() {
    super();

    /** @type {HTMLElement | null} */
    this.lockedContent = null;
    /** @type {HTMLElement | null} */
    this.purchaseContent = null;
    /** @type {HTMLButtonElement | null} */
    this.toggleButton = null;
    /** @type {HTMLFormElement | null} */
    this.form = null;
    /** @type {HTMLInputElement | null} */
    this.input = null;
    /** @type {HTMLElement | null} */
    this.error = null;
    /** @type {HTMLButtonElement | null} */
    this.signupToggle = null;
    /** @type {HTMLElement | null} */
    this.signupPanel = null;
    /** @type {HTMLFormElement | null} */
    this.signupForm = null;
    /** @type {HTMLInputElement | null} */
    this.signupEmail = null;
    /** @type {HTMLElement | null} */
    this.signupError = null;
    /** @type {HTMLElement | null} */
    this.signupSuccess = null;

    this.handleSignupToggle = this.handleSignupToggle.bind(this);
    this.handleSignupSubmit = this.handleSignupSubmit.bind(this);
    this.handleSignupInput = this.handleSignupInput.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleAccessGranted = this.handleAccessGranted.bind(this);
    this.handleStorage = this.handleStorage.bind(this);
    this.handleLockedQuickAddClick = this.handleLockedQuickAddClick.bind(this);
  }

  connectedCallback() {
    this.lockedContent = this.querySelector('[data-early-access-locked]');
    this.purchaseContent = this.querySelector('[data-early-access-purchase]');
    this.toggleButton = this.querySelector('[data-early-access-toggle]');
    this.form = this.querySelector('[data-early-access-form]');
    this.input = this.querySelector('[data-early-access-input]');
    this.error = this.querySelector('[data-early-access-error]');
    this.signupToggle = this.querySelector('[data-early-access-signup-toggle]');
    this.signupPanel = this.querySelector('[data-early-access-signup-panel]');
    this.signupForm = this.signupPanel?.querySelector('form') ?? null;
    this.signupEmail = this.signupPanel?.querySelector('input[type="email"]') ?? null;
    this.signupError = this.querySelector('[data-early-access-signup-error]');
    this.signupSuccess = this.querySelector('[data-early-access-signup-success]');

    // Suppress the native validation bubble only once the script is running,
    // so a no-JS shopper still gets the browser's own checks.
    this.signupForm?.setAttribute('novalidate', '');

    this.signupToggle?.addEventListener('click', this.handleSignupToggle);
    this.signupForm?.addEventListener('submit', this.handleSignupSubmit);
    this.signupEmail?.addEventListener('input', this.handleSignupInput);
    this.toggleButton?.addEventListener('click', this.handleToggle);
    this.form?.addEventListener('submit', this.handleSubmit);
    this.input?.addEventListener('input', this.handleInput);
    document.addEventListener(ACCESS_EVENT, this.handleAccessGranted);
    window.addEventListener('storage', this.handleStorage);

    if (this.hasStoredAccess() || this.hasLaunched()) {
      this.unlock(false);
    } else {
      this.lock();
    }

    this.showSignupSuccessIfReturned();
  }

  disconnectedCallback() {
    this.signupToggle?.removeEventListener('click', this.handleSignupToggle);
    this.signupForm?.removeEventListener('submit', this.handleSignupSubmit);
    this.signupEmail?.removeEventListener('input', this.handleSignupInput);
    this.toggleButton?.removeEventListener('click', this.handleToggle);
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.input?.removeEventListener('input', this.handleInput);
    document.removeEventListener(ACCESS_EVENT, this.handleAccessGranted);
    window.removeEventListener('storage', this.handleStorage);
    this.disableLockedQuickAdd();
    syncPageLockedState();
  }

  lock() {
    this.removeAttribute('data-access-granted');
    if (this.lockedContent) this.lockedContent.hidden = false;
    if (this.purchaseContent) this.purchaseContent.hidden = true;

    if (this.classList.contains('quick-add-access-gate')) {
      this.enableLockedQuickAdd();
    }

    syncPageLockedState();
  }

  /**
   * @param {boolean} remember
   */
  unlock(remember) {
    if (remember) {
      try {
        window.localStorage.setItem(STORAGE_KEY, STORAGE_VALUE);
      } catch (_error) {
        // Access still applies for this page when storage is unavailable.
      }
    }

    this.setAttribute('data-access-granted', '');
    if (this.lockedContent) this.lockedContent.hidden = true;
    if (this.purchaseContent) this.purchaseContent.hidden = false;
    this.disableLockedQuickAdd();
    syncPageLockedState();

    if (remember) {
      document.dispatchEvent(new CustomEvent(ACCESS_EVENT));
    }
  }

  hasStoredAccess() {
    try {
      const storedValue = window.localStorage.getItem(STORAGE_KEY);
      if (storedValue === STORAGE_VALUE) return true;
      if (storedValue !== null) window.localStorage.removeItem(STORAGE_KEY);
      return false;
    } catch (_error) {
      return false;
    }
  }

  hasLaunched() {
    const launchAt = this.dataset.launchAt || DEFAULT_LAUNCH_AT;

    const launchTime = Date.parse(launchAt);
    return Number.isFinite(launchTime) && Date.now() >= launchTime;
  }

  /**
   * Card quick-add stays visible while the product is gated so the grid keeps
   * its plus button, but a locked product must not be purchasable from the
   * card: clicks are captured and routed to the quick-add modal, which renders
   * the product page's own access-code panel.
   */
  enableLockedQuickAdd() {
    if (!this.purchaseContent) return;

    this.setAttribute('data-locked-quick-add', '');
    this.purchaseContent.hidden = false;
    this.addEventListener('click', this.handleLockedQuickAddClick, true);
  }

  disableLockedQuickAdd() {
    if (!this.hasAttribute('data-locked-quick-add')) return;

    this.removeAttribute('data-locked-quick-add');
    this.removeEventListener('click', this.handleLockedQuickAddClick, true);
  }

  /**
   * @param {Event} event
   */
  handleLockedQuickAddClick(event) {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.quick-add__button')) return;

    event.preventDefault();
    event.stopPropagation();

    // The modal is built from the product page response, so it already carries
    // that product's own early-access panel — copy is never duplicated here.
    const quickAdd = /** @type {(Element & { handleClick?: (event: Event) => unknown }) | null} */ (
      this.querySelector('quick-add-component')
    );

    if (typeof quickAdd?.handleClick === 'function') {
      quickAdd.handleClick(event);
      return;
    }

    // The quick-add module has not upgraded the element yet (or failed to
    // load): fall back to the product page, where the panel also lives.
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card')
    );
    const productLink =
      productCard?.getProductCardLink?.() || productCard?.querySelector('a.product-card__link');
    const productUrl = productLink?.getAttribute('href');

    if (productUrl) window.location.assign(productUrl);
  }

  /**
   * Reveals or hides the email capture behind the "Sign up for early access"
   * link. Shoppers without a code get a way to be told when access opens,
   * without that path competing with the unlock button above it.
   */
  handleSignupToggle() {
    if (!this.signupPanel || !this.signupToggle) return;

    const opening = this.signupPanel.hidden;

    this.signupPanel.hidden = !opening;
    this.signupToggle.setAttribute('aria-expanded', String(opening));

    if (opening) this.signupEmail?.focus();
  }

  handleSignupInput() {
    this.signupEmail?.removeAttribute('aria-invalid');
    if (this.signupError) this.signupError.hidden = true;
  }

  /**
   * Posts the signup in the background so the shopper keeps their place on the
   * product page. The form is a standard Shopify `customer` post, so the
   * address lands in Customers with email marketing consent — the subscriber
   * list — rather than anywhere theme-specific.
   *
   * The confirmation is only shown once Shopify has actually accepted the
   * signup; anything else either surfaces an inline error or falls back to a
   * native form post (which is also what carries a CAPTCHA challenge through).
   *
   * @param {SubmitEvent} event
   */
  async handleSignupSubmit(event) {
    event.preventDefault();

    const form = this.signupForm;
    const email = this.signupEmail;
    if (!form || !email) return;

    const submit = form.querySelector('button[type="submit"]');

    if (this.signupError) this.signupError.hidden = true;
    email.removeAttribute('aria-invalid');

    const value = email.value.trim();

    if (value === '') {
      this.showSignupError('Please enter your email address.');
      return;
    }

    if (!email.checkValidity() || !EMAIL_PATTERN.test(value)) {
      this.showSignupError('Please enter a valid email address.');
      return;
    }

    this.setSignupBusy(submit, email, true);

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller ? window.setTimeout(() => controller.abort(), SIGNUP_TIMEOUT_MS) : null;

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
        signal: controller ? controller.signal : undefined,
      });

      if (timeoutId) window.clearTimeout(timeoutId);

      const url = response.url || '';
      const posted =
        url.includes(SIGNUP_RETURN_PARAM + '=success') ||
        url.includes('customer_posted=true') ||
        url.includes('contact_posted=true');

      // Shopify only redirects once it has accepted the signup.
      if (response.ok && (response.redirected || posted)) {
        this.showSignupSuccess();
        return;
      }

      // A 200 without a redirect means Shopify re-rendered with a form error.
      if (response.ok) {
        this.showSignupError(await readSignupError(response));
        return;
      }

      this.submitSignupNatively();
    } catch (error) {
      if (timeoutId) window.clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        this.showSignupError('That took too long — please try again.');
        return;
      }

      this.submitSignupNatively();
    }
  }

  /**
   * Last resort when the background post cannot complete: let the browser post
   * the form for real. The field is un-frozen first — a `disabled` input is
   * omitted from the submission, which would post an empty email.
   */
  submitSignupNatively() {
    const form = this.signupForm;
    if (!form || !this.signupEmail) return;

    this.setSignupBusy(form.querySelector('button[type="submit"]'), this.signupEmail, false);
    form.submit();
  }

  /**
   * @param {Element | null} submit
   * @param {HTMLInputElement} email
   * @param {boolean} busy
   */
  setSignupBusy(submit, email, busy) {
    // `readOnly`, not `disabled`, so the value survives into a native fallback post.
    email.readOnly = busy;

    if (!(submit instanceof HTMLButtonElement)) return;

    submit.disabled = busy;
    const label = submit.querySelector('span');

    if (busy) {
      submit.setAttribute('aria-busy', 'true');

      if (label) {
        label.dataset.previousLabel = label.textContent || '';
        label.textContent = 'Adding…';
      }

      return;
    }

    submit.removeAttribute('aria-busy');

    if (label && typeof label.dataset.previousLabel === 'string') {
      label.textContent = label.dataset.previousLabel;
      delete label.dataset.previousLabel;
    }
  }

  showSignupSuccess() {
    if (this.signupPanel) this.signupPanel.hidden = true;
    if (this.signupToggle) this.signupToggle.hidden = true;
    if (this.signupError) this.signupError.hidden = true;
    if (this.signupSuccess) this.signupSuccess.hidden = false;
  }

  /**
   * @param {string} message
   */
  showSignupError(message) {
    const form = this.signupForm;

    if (this.signupEmail) {
      this.setSignupBusy(form ? form.querySelector('button[type="submit"]') : null, this.signupEmail, false);
      this.signupEmail.setAttribute('aria-invalid', 'true');
      this.signupEmail.focus();
    }

    if (this.signupError) {
      this.signupError.textContent = message;
      this.signupError.hidden = false;
    }
  }

  /**
   * Shows the confirmation when the page loads back from the native fallback
   * post, then tidies the marker out of the URL so a refresh cannot replay it.
   */
  showSignupSuccessIfReturned() {
    if (!this.signupSuccess) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get(SIGNUP_RETURN_PARAM) !== 'success') return;

    this.showSignupSuccess();

    params.delete(SIGNUP_RETURN_PARAM);
    params.delete('customer_posted');

    const query = params.toString();
    const search = query ? '?' + query : '';

    window.history.replaceState({}, '', window.location.pathname + search + window.location.hash);
  }

  handleToggle() {
    if (!this.form || !this.toggleButton) return;

    this.form.hidden = false;
    this.toggleButton.hidden = true;
    this.toggleButton.setAttribute('aria-expanded', 'true');
    this.input?.focus();
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    event.preventDefault();
    if (!this.input) return;

    if (this.input.value.trim() === ACCESS_CODE) {
      this.unlock(true);
      return;
    }

    this.input.setAttribute('aria-invalid', 'true');
    if (this.error) this.error.hidden = false;
    this.input.focus();
  }

  handleInput() {
    this.input?.removeAttribute('aria-invalid');
    if (this.error) this.error.hidden = true;
  }

  handleAccessGranted() {
    if (!this.hasAttribute('data-access-granted')) this.unlock(false);
  }

  /**
   * @param {StorageEvent} event
   */
  handleStorage(event) {
    if (event.key !== STORAGE_KEY && event.key !== null) return;

    if (this.hasStoredAccess() || this.hasLaunched()) {
      this.unlock(false);
    } else {
      this.lock();
    }
  }
}

/**
 * Turns Shopify's re-rendered signup form into a message worth showing. Shopify
 * answers a rejected signup with the page HTML rather than a status code, so
 * the specific cases have to be read out of the markup.
 *
 * @param {Response} response
 * @returns {Promise<string>}
 */
async function readSignupError(response) {
  const generic = 'That email could not be saved. Please check it and try again.';

  try {
    const html = await response.text();
    const match = html.match(/<[^>]*form[^>]*>([\s\S]*?)<\/form>/i);
    const snippet = match?.[1] ?? html;

    if (/already.*subscribed|already.*exists|has already been taken/i.test(snippet)) {
      return "You're already on the list.";
    }

    if (/invalid.*email|email.*invalid/i.test(snippet)) {
      return 'Please enter a valid email address.';
    }
  } catch (_error) {
    // Unreadable body — the generic message still tells the shopper what to do.
  }

  return generic;
}

if (!customElements.get('early-access-gate')) {
  customElements.define('early-access-gate', EarlyAccessGate);
}
